import { Navigate, Route, useParams } from 'react-router-dom'
import { useGetMachineryDirectiveCompliance } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { MachineryDirectiveComplianceStudioLayout } from './pages/MachineryDirectiveComplianceStudioPage'
import { DeclarationOfConformityPage } from './steps/declaration-of-conformity'
import { HazardsPage, HazardDetailPage } from './steps/hazards'
import { IntroPage } from './steps/intro'
import { ManualGenerationPage } from './steps/manual-generation'
import { ManualValidationPage } from './steps/manual-validation'
import { RiskAssessmentPage } from './steps/risk-assessment'
import { EhsrsOverviewPage } from './steps/ehsrs-overview'
import { RiskReductionMeasuresPage, RiskReductionMeasureDetailPage } from './steps/risk-reduction-measures'
import {
  SafetyFunctionsPage,
  SafetyFunctionDetailPage,
} from './steps/safety-functions'
import {
  AssociatedPartiesPage,
  EPlanSettingsPage,
  ProductMetadataSettingsPage,
} from './steps/settings'
import { MachineLocationsPage } from './steps/machine-locations'
import { StandardsPage } from './steps/standards'

function MachineryDirectiveComplianceRouteRedirect() {
  const params = useParams<{
    machineryDirectiveComplianceId: string
    '*': string
  }>()
  const machineryDirectiveComplianceId =
    params.machineryDirectiveComplianceId ?? ''
  const trailingPath = params['*'] ? `/${params['*']}` : ''
  const {
    data: compliance,
    isLoading,
    isError,
  } = useGetMachineryDirectiveCompliance(machineryDirectiveComplianceId)

  if (isLoading) {
    return null
  }

  if (isError || !compliance?.productId) {
    return <Navigate to='/product-catalog' replace />
  }

  return (
    <Navigate
      to={`/product-catalog/${compliance.productId}/machinery-directive/${machineryDirectiveComplianceId}${trailingPath}`}
      replace
    />
  )
}

function machineryDirectiveComplianceRouteChildren() {
  return (
    <>
      <Route index element={<IntroPage />} />
      <Route path='standards' element={<StandardsPage />} />
      <Route path='machine-locations' element={<MachineLocationsPage />} />
      <Route path='hazards' element={<HazardsPage />} />
      <Route path='hazards/:hazardId' element={<HazardDetailPage />} />
      <Route
        path='risk-reduction-measures'
        element={<RiskReductionMeasuresPage />}
      />
      <Route
        path='risk-reduction-measures/:riskReductionMeasureId'
        element={<RiskReductionMeasureDetailPage />}
      />
      <Route path='safety-functions' element={<SafetyFunctionsPage />} />
      <Route
        path='safety-functions/:safetyFunctionId'
        element={<SafetyFunctionDetailPage />}
      />
      <Route path='ehsrs-overview' element={<EhsrsOverviewPage />} />
      <Route path='risk-assessment' element={<RiskAssessmentPage />} />
      <Route path='manual-generation' element={<ManualGenerationPage />} />
      <Route path='manual' element={<ManualValidationPage />} />
      <Route
        path='declaration-of-conformity'
        element={<DeclarationOfConformityPage />}
      />
      <Route
        path='settings/associated-parties'
        element={<AssociatedPartiesPage />}
      />
      <Route
        path='settings/product-metadata'
        element={<ProductMetadataSettingsPage />}
      />
      <Route path='settings/e-plan' element={<EPlanSettingsPage />} />
      <Route
        path='settings'
        element={<Navigate to='settings/associated-parties' replace />}
      />
      <Route path='*' element={<Navigate to='' replace />} />
    </>
  )
}

export const machineryDirectiveComplianceRoute = (
  <>
    <Route
      path='machinery-directive/:machineryDirectiveComplianceId/*'
      element={<MachineryDirectiveComplianceRouteRedirect />}
    />
    <Route
      path='product-catalog/:productId/machinery-directive/:machineryDirectiveComplianceId'
      element={<MachineryDirectiveComplianceStudioLayout />}
    >
      {machineryDirectiveComplianceRouteChildren()}
    </Route>
  </>
)
